import { ACCOUNT_NOT_PROVISIONED } from '@time-tracking/shared';

import { ApiError } from '../api/client.js';
import { AuthCardHeading } from '../components/AuthCardHeading.js';
import { AuthShell } from '../components/AuthShell.js';
import { Button } from '../components/Button.js';
import { WarningIcon } from '../components/icons/WarningIcon.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useAuth } from '../auth/AuthContext.js';

/** Fixed user-facing copy. The server's message is not rendered: it is an API string, not copy
 *  for the focal line of a branded card, and the card must read the same however the API words
 *  it. "administrator" matches the role name used throughout the app. */
const NOT_PROVISIONED_MESSAGE =
  'Your UT EID signed in successfully, but it has not been set up for Timesheets yet. Contact your administrator to be added.';

/**
 * True when a failed query is the 403 the API returns for an authenticated-but-unprovisioned
 * federated user. Keyed on the shared `code`, not the human message, so rewording the API copy
 * cannot silently turn this card back into the redirect loop it exists to replace.
 */
export function isNotProvisioned(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 403 && error.code === ACCOUNT_NOT_PROVISIONED
  );
}

/**
 * Shown when Enterprise Auth authenticated someone the app has no row for — in dev/prod, where
 * `requireProvisionedUser` is on, AD sync is the only way in.
 *
 * This is a dead end by design, so the one control is Sign out rather than a retry: the session
 * is valid and retrying `/v1/me` would fail identically until an administrator seeds the row.
 * Reuses `AuthShell` for the chrome-free frame, exactly the case its docs anticipate.
 */
export function NotProvisioned() {
  const { signOut } = useAuth();
  usePageTitle('Account not set up');

  return (
    <AuthShell>
      <AuthCardHeading />
      <div className="auth-error" role="alert">
        <WarningIcon className="auth-error__icon" />
        <span>{NOT_PROVISIONED_MESSAGE}</span>
      </div>
      {/* An action, not navigation — hence a button, where the callback card uses a link. */}
      <Button variant="secondary" onClick={signOut}>
        Sign out
      </Button>
    </AuthShell>
  );
}
